import type { icons } from "../company-ui";

/**
 * The menu (ALN-25, docs/pages/app-shell.md): the mockup v24 two-level menu (G1) in bands, groups
 * and pages, filtered by the role map in C (G2). Paths match the mockup routes and the `route:` of
 * each help article, so page help (ALN-53) can find its article from the URL. Compliance's pages are
 * tabs of `/compliance` (`?tab=`).
 *
 * `entry` is the Align entry that builds the screen; until it ships the route shows "Not built yet".
 * `planned` marks pages added with G1 whose entry also writes their help article.
 */
export interface NavItem {
  path: string;
  label: string;
  /** The page's own title, when it differs from the menu label ("All programs" opens Programs). */
  title?: string;
  /** Any one of these permissions opens the page (app-shell.md C). */
  permission?: string;
  /** Roles that may open the page, when no permission says it (Monthly close). */
  roles?: string[];
  tab?: string;
  entry?: string;
  planned?: true;
  blurb: string;
}
export type GroupId = "programs" | "grow" | "make" | "inventory" | "close" | "compliance" | "setup";
export interface NavGroup { id: GroupId; label: string; hint: string; icon: keyof typeof icons; items: NavItem[] }
export interface NavBand { id: "plan" | "flow" | "finance" | "setup"; label?: string; groups: NavGroup[] }

const COSTS = "costs.view";
const comp = (tab: string, label: string, blurb: string): NavItem => ({ path: "/compliance", tab, label, permission: COSTS, entry: "ALN-46", blurb });

export const NAV: NavBand[] = [
  { id: "plan", groups: [
    { id: "programs", label: "Programs", hint: "Plan and track each wine", icon: "programs", items: [
      { path: "/programs", label: "All programs", title: "Programs", permission: COSTS, blurb: "Every wine in production: what it should cost per case, and how spending so far compares with the plan." },
      { path: "/builder", label: "Program builder", permission: COSTS, entry: "ALN-34", blurb: "Plan one wine stage by stage and watch its cost per case." },
      { path: "/templates", label: "Templates", permission: COSTS, blurb: "Start a new program from a past one with real numbers." },
      { path: "/estimate", label: "Team estimates", permission: "surveys.answer", blurb: "Blind size-then-hours estimates from the team for salaried work." },
    ] },
  ] },
  { id: "flow", label: "Vine to bottle", groups: [
    { id: "grow", label: "Grow", hint: "Vineyard to crush pad", icon: "grow", items: [
      { path: "/vineyard", label: "Vineyard blocks", entry: "ALN-60", planned: true, blurb: "Each vineyard block and its season." },
      { path: "/sprays", label: "Spray log", entry: "ALN-60", planned: true, blurb: "A spray log for each block." },
      { path: "/harvest", label: "Harvest plan", entry: "ALN-59", planned: true, blurb: "Planning the harvest, block by block." },
      { path: "/intake", label: "Fruit intake", entry: "ALN-59", planned: true, blurb: "Weigh tags for fruit as it comes in." },
      { path: "/contracts", label: "Grower contracts", entry: "ALN-59", planned: true, blurb: "Grower contracts and settlement." },
      { path: "/field", label: "Field entry", entry: "ALN-60", planned: true, blurb: "Field entry for vineyard crews, on a phone, even offline." },
    ] },
    { id: "make", label: "Make", hint: "Crush to bottle", icon: "make", items: [
      { path: "/wo", label: "Work orders", entry: "ALN-56", planned: true, blurb: "Cellar work orders, with a simple view for the crew." },
      { path: "/vessels", label: "Vessels and barrels", entry: "ALN-57", planned: true, blurb: "Tanks, vessels and barrels." },
      { path: "/lab", label: "Lab and quality", entry: "ALN-58", planned: true, blurb: "Lab results and quality checks." },
      { path: "/drygoods", label: "Additives and dry goods", entry: "ALN-61", planned: true, blurb: "Stock of additives and dry goods." },
      { path: "/bottling", label: "Bottling runs", entry: "ALN-62", planned: true, blurb: "Bottling runs." },
      { path: "/inbond", label: "Transfers in bond", entry: "ALN-87", planned: true, blurb: "Transfers of wine in bond." },
    ] },
    { id: "inventory", label: "Inventory", hint: "Bulk, bottled and case goods", icon: "inventory", items: [
      { path: "/wip", label: "WIP and inventory", permission: COSTS, blurb: "The value of wine in the building by pool and lot, and packaging on hand." },
      { path: "/casegoods", label: "Case goods", permission: COSTS, entry: "ALN-63", planned: true, blurb: "Case goods, shared with Avero Guest." },
      { path: "/physinv", label: "Physical inventory", entry: "ALN-99", planned: true, blurb: "Physical inventory counts." },
      { path: "/trace", label: "Traceability", entry: "ALN-66", planned: true, blurb: "Tracing a wine from vineyard to bottle and back, for recalls." },
    ] },
  ] },
  { id: "finance", label: "Finance", groups: [
    { id: "close", label: "Cost and close", hint: "Rates, monthly close, reports", icon: "close", items: [
      { path: "/rates", label: "Rates and pools", permission: COSTS, blurb: "Loaded labor rates, equipment and overhead pools, and their drivers." },
      { path: "/absorption", label: "Monthly close", roles: ["owner", "controller", "winemaker"], entry: "ALN-44", blurb: "Import, map, allocate, review and post the month to QuickBooks." },
      { path: "/drivers", label: "Driver trial", permission: COSTS, entry: "ALN-73", planned: true, blurb: "Trying another way to spread a cost pool side by side with the current one, before the winery adopts it." },
      { path: "/carry", label: "Absorption", permission: COSTS, entry: "ALN-38", blurb: "Over and under absorbed cost, and idle capacity, by pool." },
      { path: "/reports", label: "Reports", permission: COSTS, entry: "ALN-49", blurb: "Cost, variance and inventory reports to share." },
    ] },
    { id: "compliance", label: "Compliance", hint: "Tax, TTB, labels, bank", icon: "compliance", items: [
      comp("booktax", "Book and tax", "Book (GAAP) inventory beside the IRC 263A tax layer, and the difference."),
      comp("intercompany", "Intercompany", "Sales and charges between your companies, priced and matched."),
      comp("ttb", "TTB and excise", "The TTB report of wine premises operations and federal excise."),
      comp("ttbops", "5120.17 from operations", "The TTB 5120.17 built from the cellar's own records."),
      comp("taxclass", "Tax class changes", "Wine that moved between tax classes, and the lines it writes."),
      comp("excise", "Excise return (5000.24)", "The federal excise return, with the small producer credit."),
      comp("tabc", "Texas monthly report", "The Texas winery monthly excise report."),
      comp("labels", "Label rules", "Label minimums for vintage, variety and appellation, checked per lot."),
      comp("borrowing", "Borrowing base", "Inventory the bank lends against, at cost."),
      comp("policies", "Policies", "The accounting and tax policies everything else uses."),
    ] },
  ] },
  { id: "setup", groups: [
    { id: "setup", label: "Setup", hint: "Process, admin, help", icon: "setup", items: [
      { path: "/setup", label: "Winery setup", entry: "ALN-28", planned: true, blurb: "A checklist that walks a new winery through setting up Avero Vintner." },
      { path: "/process", label: "Process editor", permission: "process.view", blurb: "Your winery's process, with cost drivers, rates and pools on each step." },
      { path: "/admin", label: "Administration", permission: "admin.manage", blurb: "People, companies, connections and the defaults everything else uses." },
      { path: "/mapping", label: "Account mapping", entry: "ALN-29", planned: true, blurb: "Your QuickBooks accounts mapped to cost pools and departments, and what to capitalize." },
      { path: "/rulepacks", label: "Rule packs", entry: "ALN-30", planned: true, blurb: "The excise rates and rules Avero Vintner applies, published by Forward Rise." },
      { path: "/migration", label: "Opening balances", entry: "ALN-31", planned: true, blurb: "Opening balances, tied to QuickBooks and the last TTB report." },
      { path: "/help", label: "Help center", blurb: "How Avero Vintner works, and what each number means." },
    ] },
  ] },
];

export const NAV_GROUPS: NavGroup[] = NAV.flatMap(b => b.groups);
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap(g => g.items);

/** What the menu needs to know about a person: their role (or roles, with ALN-121) and permissions. */
export interface Access { role: string | null; roles?: string[]; permissions: string[] }

/** Can this person open the page? Any one of their roles is enough. The server still refuses what a role can't do. */
export function canOpen(item: NavItem, a: Access | null | undefined): boolean {
  if (!item.permission && !item.roles) return true;
  if (!a) return false;
  if (item.permission && !a.permissions.includes(item.permission)) return false;
  const roles = a.roles ?? (a.role ? [a.role] : []);
  return !item.roles || roles.some(r => item.roles!.includes(r));
}

/** The menu with what this person can't open taken out; groups and bands left empty drop out. */
export function menuFor(a: Access | null | undefined): NavBand[] {
  return NAV.map(b => ({ ...b, groups: b.groups.map(g => ({ ...g, items: g.items.filter(i => canOpen(i, a)) })).filter(g => g.items.length) }))
    .filter(b => b.groups.length);
}

export const hrefOf = (i: NavItem) => (i.tab ? `${i.path}?tab=${i.tab}` : i.path);
export const titleOf = (i: NavItem) => i.title ?? i.label;
/** Header search: every word of the query is in the menu label or in the page title ("All programs" and "Programs" both find /programs). */
export const matchesPage = (i: NavItem, query: string) => {
  const words = query.toLowerCase().split(/s+/).filter(Boolean);
  return [i.label, titleOf(i)].some(name => words.every(w => name.toLowerCase().includes(w)));
};

const tabOf = (search: string) => new URLSearchParams(search).get("tab");
/** The menu page the address is on (a Compliance tab by `?tab=`, Book and tax by default), if any. */
export function currentItem(location: string, search: string): NavItem | undefined {
  const under = NAV_ITEMS.filter(i => location === i.path || location.startsWith(`${i.path}/`));
  if (under.length <= 1) return under[0];
  const tab = tabOf(search);
  return under.find(i => i.tab === tab) ?? under[0];
}
export const groupOfItem = (item: NavItem | undefined) => (item ? NAV_GROUPS.find(g => g.items.includes(item)) : undefined);
